import { StyledRechartsResponsiveContainer } from 'pages/Dashboard/components/styled';
import {
    ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';
import { backgroundColor, primaryText } from 'styles/themeProperties';

/**
 * Fixed X-axis layer for vertical scrolling - positioned absolutely, only shows X-axis
 */
const FixedXAxisContainer = styled(StyledRechartsResponsiveContainer)`
    position: absolute;
    bottom: 0;
    left: 0;
    pointer-events: none;
    z-index: 2;

    text {
        fill: ${primaryText};
    }

    .recharts-bar {
        display: none;
    }
    .recharts-yAxis {
        display: none;
    }
    .recharts-legend-wrapper {
        display: none;
    }
    .recharts-cartesian-grid {
        display: none;
    }
`;

/**
 * Scrollable data layer for vertical - hides X-axis, shows Y-axis and bars
 */
const ScrollableVerticalDataContainer = styled(StyledRechartsResponsiveContainer)`
    .recharts-xAxis {
        display: none;
    }

    .recharts-legend-wrapper {
        display: none;
    }

    min-height: 100%;
`;

/**
 * Vertical fade overlay to indicate scrollable content
 */
const VerticalFadeOverlay = styled.div<{
    $position: 'top' | 'bottom';
    $visible: boolean;
    $offset?: string;
}>`
    position: absolute;
    left: 0;
    right: 0;
    height: 3rem;
    pointer-events: none;
    z-index: 3;
    transition: opacity 0.2s ease-in-out;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};

    background: linear-gradient(
        to ${({ $position }) => ($position === 'top' ? 'bottom' : 'top')},
        ${backgroundColor},
        transparent
    );

    ${({ $position, $offset }) =>
        $position === 'top'
            ? $offset
                ? `top: ${$offset};`
                : 'top: 0px;'
            : $offset
            ? `bottom: ${$offset};`
            : 'bottom: 0px;'}
`;

const RootContainer = styled.div<{ $width: string | number }>`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: ${({ $width }) => (typeof $width === 'number' ? `${$width}px` : $width)};
    align-items: stretch;
    min-height: 0;
`;

const NonLegendWrapper = styled.div`
    position: relative;
    flex: 1;
    height: 100%;
`;

const ScrollableBarsContainer = styled.div<{ $xAxisHeight: number }>`
    height: calc(100% - ${({ $xAxisHeight }) => $xAxisHeight}px - 4px);
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
`;

const AbsoluteHeightContentWrapper = styled.div<{ $height: number }>`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;
    height: ${({ $height }) => $height}px;
`;

const LegendContainer = styled.div`
    padding-left: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
`;

type VerticalScrollUtilsParams = {
    scrollContainerRef: React.RefObject<HTMLDivElement>;
    dataLength: number;
    totalHeight: number | `${number}%`;
    startScrolledToEnd?: boolean;
};

function useVerticalScrollUtils({
    scrollContainerRef,
    dataLength,
    totalHeight,
    startScrolledToEnd = false,
}: VerticalScrollUtilsParams) {
    const [showTopFade, setShowTopFade] = useState(false);
    const [showBottomFade, setShowBottomFade] = useState(false);

    // Check scroll position to show/hide fade overlays
    const updateScrollFades = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            setShowTopFade(false);
            setShowBottomFade(false);
            return;
        }

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        // if scrolled down from start (with 1px threshold)
        setShowTopFade(scrollTop > 1);

        // if not scrolled to end (with 1px threshold)
        setShowBottomFade(scrollTop < scrollHeight - clientHeight - 1);
    }, [scrollContainerRef]);

    useEffect(() => {
        // Scroll to the end vertically when the component mounts
        if (startScrolledToEnd && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop =
                scrollContainerRef.current.scrollHeight;
        }
        updateScrollFades();
    }, [scrollContainerRef, startScrolledToEnd, updateScrollFades]);

    // Update fades when data length changes (affects scrollability)
    useEffect(() => {
        updateScrollFades();
    }, [dataLength, totalHeight, updateScrollFades]);

    // Handle vertical scroll with regular mouse wheel (no conversion needed)
    const handleWheel = useCallback(
        (_e: WheelEvent) => {
            if (!scrollContainerRef.current) {
                return;
            }

            // Let normal vertical scrolling work, just update fades
            updateScrollFades();
        },
        [scrollContainerRef, updateScrollFades]
    );

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        container.addEventListener('wheel', handleWheel, { passive: true });
        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [handleWheel, scrollContainerRef]);

    return {
        showTopFade,
        showBottomFade,
        updateScrollFades,
    };
}

type VerticalScrollableBarChartProps = {
    /**
     * Render function that returns the BarChart configuration
     * Should include all XAxis, YAxis, Bar, and other Recharts components
     * The function receives a 'layer' param: 'fixed-xaxis' or 'scrollable-data'
     */
    children: (p: {
        layer: 'fixed-xaxis' | 'scrollable-data';
        xAxisHeight: number | undefined;
    }) => ReactElement;
    /**
     * Optional legend element to render beside the scrollable chart
     */
    legend?: ReactElement;
    /**
     * Height of the X-axis area in pixels (default: 30)
     * The scrollable container will be offset by this amount
     */
    xAxisHeight?: number;
    /**
     * Number of data items (bars) to display
     * Used with minBarHeightPercent to calculate total scroll height
     */
    dataLength: number;
    /**
     * Minimum height per bar as pixels or as a percentage of the scrollable container (default: 10%)
     * E.g., 10 means each bar needs 10% of the visible area height
     */
    minBarHeight?: number | `${number}%`;
    /**
     * Width of the chart container (default: "100%")
     */
    width?: string | number;
    /**
     * Whether the bars should start scrolled all the way to the end
     */
    startScrolledToEnd?: boolean;
};

/**
 * VerticalScrollableBarChart - A reusable component for vertically scrollable horizontal bar charts
 *
 * Use this for horizontal bar charts (layout="vertical") with many categories that need vertical scrolling
 *
 * @example
 * ```tsx
 * <VerticalScrollableBarChart
 *     xAxisHeight={30}
 *     dataLength={chartData.length}
 *     minBarHeightPercent={8}
 * >
 *     {({ layer, xAxisHeight }) => (
 *         <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 15, left: 15, bottom: 5 }}>
 *             <CartesianGrid strokeDasharray="3 3" vertical={false} />
 *             <XAxis type="number" height={xAxisHeight} />
 *             <YAxis type="category" dataKey="name" width={150} />
 *             <Bar dataKey="value" fill={theme.dashboardAccentPrimary} />
 *         </BarChart>
 *     )}
 * </VerticalScrollableBarChart>
 * ```
 */
export function VerticalScrollableBarChart({
    children,
    legend,
    xAxisHeight = 30,
    dataLength,
    minBarHeight = '10%',
    width = '100%',
    startScrolledToEnd = false,
}: VerticalScrollableBarChartProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    type ScrollableSizing =
        | 'content-bigger-than-container'
        | 'content-smaller-than-container';
    const [scrollableSizing, setScrollableSizing] = useState<ScrollableSizing>();

    // Calculate total height as percentage or as a fixed value depending on the input
    const totalHeight =
        typeof minBarHeight === 'string'
            ? ((Math.max(
                  100,
                  dataLength * Number(minBarHeight.substring(0, minBarHeight.length - 1))
              ) + '%') as `${number}%`)
            : dataLength * minBarHeight;

    const onResize = useCallback(() => {
        // Recharts ResponsiveContainer struggles when given an absolut height (at least to do what we want)
        // so, for absolute min bar widths (yielding an absolute totalHeight), we'll conditionally swap in
        // our own container instead, when necessary to get the scaling we want
        if (typeof totalHeight !== 'number') {
            return;
        }
        setScrollableSizing(
            totalHeight >= (scrollContainerRef.current?.clientHeight ?? 0)
                ? 'content-bigger-than-container'
                : 'content-smaller-than-container'
        );
    }, [totalHeight]);

    useLayoutEffect(() => {
        onResize();
    }, [onResize]);

    // Use the scroll fades hook to manage fade overlay visibility
    const { showTopFade, showBottomFade, updateScrollFades } = useVerticalScrollUtils({
        scrollContainerRef,
        dataLength,
        totalHeight,
        startScrolledToEnd,
    });

    return (
        <RootContainer $width={width}>
            <NonLegendWrapper>
                {/* Fixed X-axis layer - absolutely positioned, full height */}
                <FixedXAxisContainer width="100%" height="100%">
                    {children({ layer: 'fixed-xaxis', xAxisHeight })}
                </FixedXAxisContainer>

                {/* Fade overlays to indicate scrollable content */}
                <VerticalFadeOverlay $position="top" $visible={showTopFade} />
                <VerticalFadeOverlay
                    $position="bottom"
                    $visible={showBottomFade}
                    $offset={`calc(4px + ${xAxisHeight}px)`}
                />

                {/* Scrollable data + Y-axis layer - offset from bottom by X-axis height */}
                <ScrollableBarsContainer
                    $xAxisHeight={xAxisHeight}
                    ref={scrollContainerRef}
                    onScroll={updateScrollFades}
                >
                    {typeof totalHeight !== 'number' ||
                    scrollableSizing !== 'content-bigger-than-container' ? (
                        <ScrollableVerticalDataContainer
                            height={
                                typeof totalHeight !== 'number' ? totalHeight : undefined
                            }
                            onResize={onResize}
                        >
                            {children({ layer: 'scrollable-data', xAxisHeight: 0 })}
                        </ScrollableVerticalDataContainer>
                    ) : (
                        <AbsoluteHeightContentWrapper $height={totalHeight}>
                            <ScrollableVerticalDataContainer onResize={onResize}>
                                {children({
                                    layer: 'scrollable-data',
                                    xAxisHeight: 0,
                                })}
                            </ScrollableVerticalDataContainer>
                        </AbsoluteHeightContentWrapper>
                    )}
                </ScrollableBarsContainer>
            </NonLegendWrapper>

            {/* Fixed legend below the chart */}
            {legend && <LegendContainer>{legend}</LegendContainer>}
        </RootContainer>
    );
}
